import type {ReactNode} from 'react'

/**
 * Common shape every actor presented to `ActorListEditor` must conform to.
 * - `globalRelayId` is the preferred stable identity used for selection /
 *   dedup / list keying. When omitted, the editor falls back to a key
 *   derived from `actorType` + `actorId`.
 * - `name` is the raw actor name (role slug, team slug, app name, etc.).
 *   Repository-role names are humanized via `humanizeRoleName` for display
 *   so callers don't need to pre-format.
 * - `actorType` is the structural type (e.g. `Team`, `Integration`,
 *   `RepositoryRole`). Used to compute the `@team` / integration-owner
 *   tail of the description and to fall back to a humanized type label
 *   when category metadata is unavailable.
 * - `actorCategory` is the server-supplied category used by the segmented
 *   filter and the leading portion of the description.
 * - `owner` is the integration owner used in the description for
 *   `Integration`-typed actors.
 * - `actorId` and `preferredAvatarUrl` are consumed by `ActorAvatar` to
 *   render the leading visual; supply whichever field(s) the server
 *   returns for the given actor type.
 */
export interface Actor {
  globalRelayId?: string
  name: string | null
  actorType: string
  actorCategory?: string
  owner?: string
  actorId?: string | number | null
  preferredAvatarUrl?: string
}

/**
 * Stable identity used by the editor for selection / dedup / list keying.
 * Prefers `globalRelayId` when present and falls back to a composite of
 * `actorType` + `actorId` so legacy actor shapes still work.
 */
export function actorKey(actor: Actor): string {
  return actor.globalRelayId ?? `${actor.actorType}:${actor.actorId}`
}

/** Async data source for the dropdown's suggestions. */
export interface ActorSelectorSource<TActor extends Actor> {
  /** Fetch a fresh suggestions list for the given filter string. */
  fetchSuggestions: (query: string) => Promise<TActor[]>
  /** Defaults to "Add actors". */
  addButtonLabel?: string
  /** Placeholder text for the dropdown's filter input. */
  panelPlaceholder?: string
  /**
   * Optional override for the dropdown panel title. Receives the active
   * category (or `null` when "All" is selected). Defaults to "Add actors"
   * / "Add {category} actors".
   */
  panelTitle?: (activeCategory: string | null) => string
  /** Optional subtitle below the panel title. */
  panelSubtitle?: string
}

/**
 * Async data source for the canonical category list shown in the segmented
 * filter. When `fetchCategories` is omitted, fails, or returns an empty
 * array, the category filter is hidden entirely.
 */
export interface ActorCategorySource {
  fetchCategories?: () => Promise<string[]>
}

export interface ActorListEditorEmptyState {
  /** Heading shown when the unfiltered list is empty. */
  heading: string
  /**
   * Optional override for the heading shown when the active category filter
   * yields zero rows. Receives the active category name. Defaults to
   * `"No {category} actors have been added."`.
   */
  filteredHeading?: (category: string) => string
}

/**
 * Category filter presentation. `'segmented'` always renders an inline button row;
 * `'responsive'` renders that row but collapses to a dropdown on narrow viewports.
 */
export type CategoryFilterVariant = 'segmented' | 'responsive'

export interface ActorListEditorProps<TActor extends Actor> {
  /** Box header title (e.g. "Actors", "Allowed actors"). */
  title: string
  /** Optional subtitle rendered above the box (used by `BypassListPanel`). */
  subtitle?: ReactNode
  /** When true, the segmented filter and add dropdown are hidden. */
  readOnly?: boolean
  /** The current actor list to render and from which selection is derived. */
  actors: TActor[]
  selector: ActorSelectorSource<TActor>
  /**
   * Base URL used by `ActorAvatar` to construct avatar image URLs for
   * actors that don't carry a fully-formed `preferredAvatarUrl`. Required
   * when any actor of type `Team`, `Integration`, or `User` is rendered
   * without a `preferredAvatarUrl`.
   */
  baseAvatarUrl?: string
  /** Omit to disable the category filter entirely. */
  categories?: ActorCategorySource
  /** Category filter presentation. Defaults to `'segmented'` (inline at all widths). */
  categoryFilterVariant?: CategoryFilterVariant
  /** Toggle the actor in the list. Called immediately on dropdown selection. */
  onAdd: (actor: TActor) => void
  /** Remove the actor from the list. Called from the row trash button or by toggling off in the dropdown. */
  onRemove: (actor: TActor) => void
  /**
   * Optional render slot for per-row trailing controls (e.g. bypass mode
   * editor). The default delete `IconButton` is rendered by the editor and
   * appears after these.
   */
  renderRowActions?: (actor: TActor) => ReactNode
  /** Headings for the empty state. */
  emptyState?: ActorListEditorEmptyState
}
